/**
 * modals.ts — reusable modal helper plus the offline-earnings and prestige-preview
 * dialogs. These replace the old welcome toast and the bare window.confirm.
 */
import { el } from './dom';
import { fmt, type UIContext } from './context';
import { format, formatDuration } from '../core/numbers';
import { prestigeGain } from '../systems/prestige';
import { ascensionGain } from '../systems/ascension';
import { canClaimDaily, claimDaily } from '../systems/daily';
import { BALANCE } from '../data';

interface ModalHandle {
  root: HTMLElement;
  open: () => void;
  close: () => void;
}

/** Build a centered modal overlay around `content`. Click-outside and a close on demand. */
export function makeModal(content: HTMLElement, onClose?: () => void): ModalHandle {
  const panel = el('div', { class: 'modal' }, content);
  const root = el('div', { class: 'modal-overlay hidden' }, panel);
  const close = (): void => {
    root.classList.add('hidden');
    onClose?.();
    setTimeout(() => root.remove(), 250);
  };
  root.addEventListener('click', (e) => { if (e.target === root) close(); });
  return {
    root,
    open: () => requestAnimationFrame(() => root.classList.remove('hidden')),
    close,
  };
}

/** "Welcome back" — summarises offline progress. */
export function showOfflineModal(
  host: HTMLElement,
  ctx: UIContext,
  summary: { ms: number; moneyGained: number; oreGained: number; cashDelta: number },
): void {
  const rows = el('div', { class: 'offline-stats' },
    statLine('⏱️', 'Abwesend', formatDuration(summary.ms / 1000)),
    summary.oreGained > 0 ? statLine('⛏️', 'Erz abgebaut', format(summary.oreGained)) : null,
    summary.moneyGained > 0 ? statLine('💰', 'Verdient', `$${fmt(ctx, summary.moneyGained)}`) : null,
  );
  const content = el('div', { class: 'offline-modal' },
    el('div', { class: 'offline-icon', text: '🌙' }),
    el('h2', { text: 'Willkommen zurück, Boss!' }),
    el('p', { class: 'offline-sub', text: 'Deine Truppe hat weitergearbeitet, während du weg warst.' }),
    rows,
  );
  const m = makeModal(content);
  const collect = el('button', { class: 'op-btn primary big', text: '⛏️ Zurück an die Arbeit', onClick: () => m.close() });
  content.appendChild(collect);
  host.appendChild(m.root);
  m.open();
  ctx.sfx.play('reward');
}

/** Prestige confirmation with a preview of the gain, the new multiplier, and what carries over. */
export function showPrestigeModal(host: HTMLElement, ctx: UIContext, onConfirm: () => void): void {
  const gain = prestigeGain(ctx.engine.state);
  const curPts = ctx.engine.state.prestige.points;
  const newMult = 1 + (curPts + gain) * BALANCE.prestige.pointMult;

  const content = el('div', { class: 'prestige-modal' },
    el('div', { class: 'prestige-modal-icon', text: '✨' }),
    el('h2', { text: 'Prestige?' }),
    el('div', { class: 'prestige-preview' },
      statLine('✨', 'Prestige-Punkte', `+${format(gain)}`),
      statLine('🚀', 'Neuer Globalbonus', `×${newMult.toFixed(2)}`),
    ),
    el('div', { class: 'prestige-note' },
      el('div', { class: 'keep', text: '✓ Behält: Prestige-Punkte, Fähigkeiten, Trophäen' }),
      el('div', { class: 'lose', text: '✗ Zurück: Geld, Erz, Gebäude, Arbeiter, Upgrades, Forschung, Tiefe' }),
    ),
  );
  const m = makeModal(content);
  const actions = el('div', { class: 'modal-actions' },
    el('button', { class: 'op-btn', text: 'Noch nicht', onClick: () => m.close() }),
    el('button', { class: 'prestige-btn confirm', text: `Prestige (+${format(gain)})`, onClick: () => { m.close(); onConfirm(); } }),
  );
  content.appendChild(actions);
  host.appendChild(m.root);
  m.open();
}

/** Ascension confirmation — banks Cores and resets the prestige layer + run. */
export function showAscendModal(host: HTMLElement, ctx: UIContext, onConfirm: () => void): void {
  const gain = ascensionGain(ctx.engine.state);
  const cur = ctx.engine.state.ascension.cores;
  const newMult = 1 + (cur + gain) * BALANCE.ascension.coreMult;

  const content = el('div', { class: 'prestige-modal ascend' },
    el('div', { class: 'prestige-modal-icon', text: '🌌' }),
    el('h2', { text: 'Aufstieg?' }),
    el('div', { class: 'prestige-preview' },
      statLine('🌌', 'Kerne', `+${format(gain)}`),
      statLine('🚀', 'Neuer Globalbonus', `×${newMult.toFixed(2)}`),
    ),
    el('div', { class: 'prestige-note' },
      el('div', { class: 'keep', text: '✓ Behält: Kerne, Perks, Trophäen, Einstellungen' }),
      el('div', { class: 'lose', text: '✗ Zurück: Prestige-Punkte, Fähigkeiten UND dein ganzer Lauf' }),
    ),
  );
  const m = makeModal(content);
  const actions = el('div', { class: 'modal-actions' },
    el('button', { class: 'op-btn', text: 'Noch nicht', onClick: () => m.close() }),
    el('button', { class: 'ascend-btn confirm', text: `Aufsteigen (+${format(gain)})`, onClick: () => { m.close(); onConfirm(); } }),
  );
  content.appendChild(actions);
  host.appendChild(m.root);
  m.open();
}

/** Daily-login bonus modal. */
export function showDailyModal(host: HTMLElement, ctx: UIContext): void {
  const now = ctx.engine.clock.now();
  const available = canClaimDaily(ctx.engine.state, now);
  const streak = ctx.engine.state.daily.streak;

  const content = el('div', { class: 'offline-modal' },
    el('div', { class: 'offline-icon', text: '🎁' }),
    el('h2', { text: 'Täglicher Bonus' }),
    el('p', { class: 'offline-sub', text: available ? `Login-Serie: ${streak} Tag(e) — längere Serien zahlen mehr!` : 'Heute schon abgeholt. Komm morgen wieder!' }),
  );
  const m = makeModal(content);
  const claim = el('button', {
    class: 'op-btn primary big',
    text: available ? '🎁 Bonus abholen' : 'Komm morgen wieder',
    disabled: !available,
    onClick: () => {
      const r = claimDaily(ctx.engine.state, ctx.engine.clock.now(), ctx.bus);
      if (r) ctx.sfx.play('reward');
      m.close();
    },
  });
  content.appendChild(claim);
  host.appendChild(m.root);
  m.open();
}

function statLine(icon: string, label: string, value: string): HTMLElement {
  return el('div', { class: 'offline-stat' },
    el('span', { class: 'os-icon', text: icon }),
    el('span', { class: 'os-label', text: label }),
    el('span', { class: 'os-value', text: value }),
  );
}
